<template>
  <div>
    <b-card no-body class="p-3 d-inline-flex flex-row w-100 mb-4">
      <div
        class="p-3 avatar-icon text-success-dark bg-success-dark bg-success-dark-10 rounded-pill flex-shrink-0 align-self-center"
      >
        <font-awesome-icon icon="file-invoice-dollar" size="xl" />
      </div>
      <div class="flex-grow-1 ml-3">
        <label for="valor-field" class="d-inline-block text-default lh-1 mb-0">
          Valor base do plano
        </label>
        <view-toggle initial-value reveal-class="w-75" class="w-100 lh-1">
          <template #reveal="{ on }">
            <div class="d-inline-flex">
              <number-format
                id="valor-field"
                v-model.number="plano.valor"
                placeholder="Ex.: R$ 100,00"
                name="valor"
                class="align-self-center col-12"
                size="sm"
                :state="validateState('valor')"
              />
              <b-button
                size="sm"
                class="flex-shrink-0 ml-2 px-2"
                variant="exitos"
                :loading="isBusy"
                @click="saveField(on)"
              >
                <font-awesome-icon icon="check" class="mr-1" />
                Salvar
              </b-button>
            </div>
          </template>
          <template #default="{ off }">
            <div
              class="d-inline-flex pr-3 cursor-pointer align-items-center"
              @click="off()"
            >
              <h2
                class="h2 d-inline mb-0 font-weight-600"
                v-text="formattedBaseValue"
              />
              <font-awesome-icon icon="pen" class="ml-1 text-exitos" />
            </div>
          </template>
        </view-toggle>
        <span class="text-muted small mb-0">
          Os descontos dos ciclos são calculados sobre este valor
        </span>
      </div>
    </b-card>
    <div class="d-flex align-items-center mb-2 w-100">
      <h3 class="mb-0">Preços e Condições</h3>
      <div class="ml-auto">
        <b-button
          variant="exitos"
          :disabled="!isAllowAddPeriodicities"
          @click="openModal()"
        >
          <font-awesome-icon icon="plus" />
          Adicionar Ciclo
        </b-button>
      </div>
    </div>
    <b-card no-body>
      <b-table
        hover
        :items="list"
        :fields="fields"
        :busy="isBusy"
        :tbody-tr-class="rowClass"
        show-empty
      >
        <template #head()="{ label, column }">
          <font-awesome-icon
            v-if="getIconHeadByKey(column)?.icon"
            :icon="getIconHeadByKey(column)?.icon"
            :style="{ color: getIconHeadByKey(column)?.color }"
          />
          <span class="text-default ml-1" v-text="label" />
        </template>
        <template #cell(status)="{ item }">
          <label class="mb-0">
            <b-form-checkbox
              switch
              inline
              button-variant="secondary"
              :checked="item.status"
              class="switch-status cursor-pointer d-flex flex-row mr-0"
              @change="changeStatus($event, item)"
            />
          </label>
        </template>
        <template #cell(periodicidade)="{ value, item }">
          <h5 class="h4 font-weight-600 text-capitalize-first mb-0">
            {{ value.text }}
            <b-badge
              v-if="item?.is_default"
              variant="success-dark"
              class="lh-1 ms-1"
              pill
            >
              <font-awesome-icon icon="check" class="mr-1" />
              Ciclo padrão
            </b-badge>
          </h5>
          <span
            class="text-body text-muted mb-0 text-sm"
            v-text="value.description"
          />
        </template>
        <template #cell(desconto_boleto)="{ value }">
          <h5 class="h4 font-weight-600 mb-0" v-text="value?.currencyValue" />
          <span :class="value?.descriptionClass" v-text="value?.description" />
        </template>
        <template #cell(desconto_pix)="{ value }">
          <h5 class="h4 font-weight-600 mb-0" v-text="value?.currencyValue" />
          <span :class="value?.descriptionClass" v-text="value?.description" />
        </template>
        <template #cell(desconto_cc)="{ value }">
          <h5 class="h4 font-weight-600 mb-0" v-text="value?.currencyValue" />
          <span :class="value?.descriptionClass" v-text="value?.description" />
        </template>
        <template #cell(actions)="row">
          <Actions
            :item="row.item"
            :view_show="false"
            @edit="handleEdit"
            @delete="handleDelete"
          />
        </template>

        <template #empty="{}">
          <h5 class="text-center">Nenhum item encontrado</h5>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
      </b-table>
    </b-card>

    <b-modal
      id="modal-periodicidade-plano"
      ref="modal-periodicidade-plano"
      size="xl"
      centered
      ok-title="Salvar"
      cancel-title="Cancelar"
      scrollable
      body-class="pt-0"
      ok-variant="exitos"
      :title="modalTitle"
      :ok-disabled="isBusy"
      @ok="handleSave"
      @hidden="resetModal"
    >
      <FormPeriodicidade
        ref="form-periodicidade"
        v-model="selectedItem"
        :active-periodicities="activePeriodicities"
        :base-value="plano.valor"
      />
    </b-modal>
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import { minValue, required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import debounce from "lodash/debounce.js";

import Actions from "@/components/Table/Actions.vue";
import FormPeriodicidade from "./FormPeriodicidade.vue";
import { getEmptyPeriodicidade } from "@/modules/planos/Components/Form/getEmptyPeriodicidade";
import {
  getPeriodiciByName,
  getPeriodicidades,
} from "@/modules/planos/Components/Form/getPeriodicidades";
import { safeArray } from "@/helpers/functions/ArrayHelpers.js";
import { deepCopy } from "@/helpers/functions/ObjectHelpers.js";
import { calcDesconto } from "@/modules/planos/helpers/calcDesconto.js";
import { calcParcelas } from "@/modules/planos/helpers/calcParcelas.js";
import { roundMoney } from "@/modules/planos/helpers/roundMoney.js";
import ViewToggle from "@/components/ViewToggle.vue";
import NumberFormat from "@/components/Form/NumberFormat.vue";

export default {
  components: { Actions, FormPeriodicidade, ViewToggle, NumberFormat },
  mixins: [validationMixin],
  data: () => ({
    list: [],
    isBusy: false,
    selectedItem: null,
  }),
  emits: ["open-first-tab", "save"],
  validations: {
    plano: {
      valor: {
        required,
        min: minValue(0),
      },
    },
  },
  computed: {
    ...mapState("planos", ["plano"]),
    modalTitle() {
      return this.selectedItem?.id ? "Editar ciclo" : "Novo ciclo";
    },
    fields() {
      return [
        {
          key: "status",
          label: "Ativo",
          class: "col-auto text-center align-middle title-status",
        },
        {
          key: "periodicidade",
          label: "Ciclo",
          class: "col-5",
          formatter: (value) => {
            const response = getPeriodiciByName(value) ?? {};
            const isOne = response?.months === 1;
            const suffix = isOne ? "mês" : "meses";

            return {
              ...response,
              description: `${response?.months} ${suffix}`,
            };
          },
        },
        {
          key: "desconto_boleto",
          label: "Boleto",
          class: "col-auto",
          formatter: (value, _, item) =>
            this.formatDiscountValue(value, item, "total_boleto"),
        },
        {
          key: "desconto_pix",
          label: "Pix",
          class: "col-auto",
          formatter: (value, _, item) =>
            this.formatDiscountValue(value, item, "total_pix"),
        },
        {
          key: "desconto_cc",
          label: "Cartão de crédito",
          class: "col-auto",
          formatter: (value, _, item) =>
            this.formatDiscountOnCreditCard(value, item, "total_cc"),
        },
        { key: "actions", label: "", class: "cell-actions" },
      ];
    },
    activePeriodicities() {
      return safeArray(this.list).map((item) => item.periodicidade);
    },
    isAllowAddPeriodicities() {
      const activePeriodicities = this.activePeriodicities;
      const availablePeriodicities = getPeriodicidades().filter(
        ({ value }) => value && !activePeriodicities.includes(value)
      );

      return availablePeriodicities.length > 0;
    },
    formattedBaseValue() {
      return this.formatCurrency(this.plano?.valor ?? 0);
    },
  },
  methods: {
    ...mapActions("planos", [
      "ActionGetPeriodic",
      "ActionSavePeriodic",
      "ActionDeletePeriodic",
    ]),
    formatCurrency(value) {
      if (typeof value !== "number") return value || "";
      const newValue = deepCopy(value);
      return `R$ ${this.$options.filters.number_format(newValue, "0,000.00")}`;
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === "") return "";
      const newValue = deepCopy(value);
      return `${this.$options.filters.number_format(newValue, "0,000.00")}%`;
    },
    formatDiscountValue(discountPercent, item, totalField) {
      const baseValue = Number(item?.valor_total ?? 0);
      const totalApi = item?.[totalField];

      // Exibe o valor que a API cobra (total_*); o cálculo local é só fallback.
      const { value, discount } = Number.isFinite(totalApi)
        ? { value: totalApi, discount: roundMoney(baseValue - totalApi) }
        : calcDesconto({ baseValue, valuePercent: Number(discountPercent) });

      const discountValue = this.formatCurrency(discount);

      const hasDiscount = discount > 0;
      const textWithDiscount = `${discountValue} de desconto`;
      const description = hasDiscount ? textWithDiscount : "Sem desconto";
      const descriptionClass = hasDiscount
        ? "text-success-dark font-weight-600"
        : "text-body text-muted";

      return {
        percent: this.formatPercent(discountPercent),
        currencyValue: this.formatCurrency(value),
        discountValue,
        description,
        descriptionClass,
        hasDiscount,
        value,
      };
    },
    formatDiscountOnCreditCard(percent, item, totalField) {
      const response = this.formatDiscountValue(percent, item, totalField);

      if (!!item?.parcelas_credito) {
        const installmentValue = calcParcelas({
          installments: Number(item?.parcelas_credito ?? 1),
          value: Number(response?.value ?? 0),
        });

        const formattedInstallment = this.formatCurrency(installmentValue);

        response.description = `Até ${item.parcelas_credito}x de ${formattedInstallment}`;
        response.descriptionClass = "text-body text-muted font-weight-600";
      }

      return response;
    },
    getIconHeadByKey(key) {
      const response = {
        desconto_cc: {
          icon: ["far", "credit-card"],
          color: "#F79E1B",
        },
        desconto_pix: {
          icon: ["fab", "pix"],
          color: "#32BC63",
        },
        desconto_boleto: {
          icon: ["fas", "barcode"],
          color: "#172b4d",
        },
      };

      if (!Reflect.has(response, key)) return {};
      return Reflect.get(response, key);
    },
    handleEdit(item) {
      this.openModal(item);
    },
    async handleDelete(item) {
      if (!item?.id) return;

      try {
        await this.ActionDeletePeriodic(item.id);
        this.$notifications.addNotification({
          message: "Ciclo removido com sucesso",
          type: "success",
        });
        await this.loadData();
      } catch (error) {
        this.$notifications.addNotification({
          message: "Erro ao remover ciclo",
          type: "danger",
        });
      }
    },
    openModal(item = null) {
      if (!item && !this.isAllowAddPeriodicities) return;

      this.selectedItem = item ? cloneDeep(item) : getEmptyPeriodicidade();
      this.$bvModal.show("modal-periodicidade-plano");
    },
    async changeStatus(isChecked, data = {}) {
      const payload = cloneDeep(data);
      payload.status = isChecked;

      try {
        this.isBusy = true;
        await this.ActionSavePeriodic(payload);
        await this.loadData();

        this.$notifications.addNotification({
          message: "Status do ciclo atualizado com sucesso!",
          type: "success",
        });
      } catch (_error) {
        this.$notifications.addNotification({
          message: "Erro ao tentar atualizar status",
          type: "danger",
        });
      } finally {
        this.isBusy = false;
      }
    },
    async handleSave(event) {
      event.preventDefault();

      const form = this.$refs["form-periodicidade"];
      const payload = await form?.save();

      if (!payload) return;

      payload.plano_exitos_id = this.$route?.params?.id;
      this.isBusy = true;

      try {
        await this.ActionSavePeriodic(payload);
        this.$notifications.addNotification({
          message: "Ciclo salvo com sucesso",
          type: "success",
        });

        await this.loadData();
        this.$nextTick(() => {
          this.$bvModal.hide("modal-periodicidade-plano");
        });
      } catch (error) {
        this.$notifications.addNotification({
          message: "Erro ao salvar ciclo",
          type: "danger",
        });
      } finally {
        this.isBusy = false;
      }
    },
    resetModal() {
      this.selectedItem = null;
      this.$refs["form-periodicidade"]?.reset();
    },
    isRowInactive(item) {
      return !!item?.status;
    },
    rowClass(item, type) {
      if (!item || type !== "row") return "";
      return this.isRowInactive(item) ? "" : "row-disabled";
    },
    openBasicData(e) {
      e?.currentTarget?.blur?.();
      this.$emit("open-first-tab");
    },
    callDebouncedLoadData: debounce(function () {
      this.loadData();
    }, 450),
    saveField(handler) {
      this.isBusy = true;
      this.$emit("save");
      handler();
      this.callDebouncedLoadData();
    },
    validateState(field) {
      const { $dirty, $error } = this.$v?.plano?.[field];
      return $dirty ? !$error : null;
    },
    isValid() {
      return true;
    },
    reset() {},
    async loadData() {
      if (!this.$route?.params?.id) return;

      this.isBusy = true;
      try {
        const params = {
          plano_exitos_id: this.$route.params.id,
          per_page: 10,
        };

        const response = await this.ActionGetPeriodic(params);
        this.list = safeArray(response.data);
      } finally {
        this.isBusy = false;
      }
    },
  },
  created() {
    this.loadData();
  },
};
</script>

<style scoped lang="scss">
.lh-1 {
  line-height: 13.4px;
}

.avatar-icon {
  aspect-ratio: 1;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .row-disabled {
  [role="cell"]:not(.cell-actions):not(.title-status) {
    opacity: 0.5;
  }
}

::v-deep .switch-status {
  min-width: fit-content;

  .custom-control-label {
    &::before,
    &::after {
      cursor: pointer;
    }
  }
}
</style>
